import classNames from 'classnames';
import * as _ from 'lodash';
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronDownIcon, TickIcon } from '@icons';
import FilterIcon from '@icons/FilterIcon';
import { setupForDark } from '@theme';
import { useMediaQuery } from '@uidotdev/usehooks';
import BottomSheet from './BottomSheet';
import ContextMenu from './ContextMenu';
import ContextMenuItem from './ContextMenuItem';
import InputErrorMessage from './InputErrorMessage';

interface Choice {
  label: string | ReactNode;
  value: string;
  dataTestId?: string;
}

export declare type DisplayMode =
  | 'filter'
  | 'dropdown'
  | 'minimalistic'
  | 'classical'
  | 'task-filter';

function Dropdown({
  title,
  value,
  choices,
  triggerClassName = '',
  choicesClassName = '',
  onChange,
  onError = () => {},
  displayMode = 'dropdown',
  singleChoice = true,
  className = ''
}: {
  title: string;
  value: string | string[];
  choices: Choice[];
  triggerClassName?: string;
  choicesClassName?: string;
  onChange: (value: string | string[]) => void;
  onError?: (error: string) => void;
  displayMode?: DisplayMode;
  singleChoice?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);
  const [optionsState, setOptionsState] = useState({
    show: false,
    position: { x: 0, y: 0 }
  });
  const isSmOrHigher = useMediaQuery('(max-width: 640px)');
  const isMobile = useMemo(() => {
    return isSmOrHigher;
  }, [isSmOrHigher]);

  const [rect, setRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [inputValue, setInputValue] = useState(null);
  const [error, setError] = useState<string | null>(null);
  const onErrorMessage = (error: string) => {
    if (onError) {
      onError(error);
    }
    setError(error);
  };

  const validate = (value: string | string[]): boolean => {
    if (choices && choices.length > 0) {
      const selected = Array.isArray(value) ? value : [value];
      const allValues = choices.map((c) => c.value);
      if (_.intersection(selected, allValues).length === 0) {
        onErrorMessage(t('components.dropdown.errorMissingValue'));
        return false;
      }
    }

    onErrorMessage(null);
    return true;
  };

  const updateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
    }
  };

  useEffect(() => {
    updateDropdownPosition();
  }, [dropdownRef?.current]);

  useLayoutEffect(() => {
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('zoom', updateDropdownPosition);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('zoom', updateDropdownPosition);
    };
  }, []);

  const labelsIndex = useMemo(() => {
    const labelsIndex = {};
    choices.forEach((choice) => {
      labelsIndex[choice.value] = choice.label;
    });
    return labelsIndex;
  }, [choices]);

  useEffect(() => {
    if (value !== inputValue) {
      if (singleChoice) {
        setInputValue(value);
      } else {
        setInputValue(
          (value as string[]).filter((v) => choices.findIndex((c) => c.value === v) !== -1)
        );
      }
    }
  }, [value, choices, singleChoice]);

  // validate every time the value or choices change
  // to make sure that they can work together
  useEffect(() => {
    validate(inputValue);
  }, [inputValue, choices]);

  const onChoiceChange = (choice: Choice) => {
    // SINGLE CHOICE
    if (singleChoice) {
      setInputValue(choice.value);
      onChange(choice.value);
      setOptionsState({
        show: false,
        position: { x: 0, y: 0 }
      });

      // MULTIPLE CHOICES
    } else {
      const index = inputValue.indexOf(choice.value);
      let value = [...inputValue];
      if (index === -1) {
        value = [...inputValue, choice.value];
      } else {
        value = inputValue.filter((v) => v !== choice.value);
      }
      // apply the sorting of the original array
      value.sort((a, b) => {
        return choices.findIndex((c) => c.value === a) - choices.findIndex((c) => c.value === b);
      });
      setInputValue(value);
      onChange(value);
    }
  };

  const isSelected = (choice): boolean => {
    if (!inputValue) {
      return false;
    }

    // SINGLE CHOICE
    if (singleChoice) {
      return inputValue === choice.value;

      // MULTIPLE CHOICES
    } else {
      return inputValue.indexOf(choice.value) !== -1;
    }
  };
  const showSelected = (value: string | string[]): string => {
    // SINGLE CHOICE
    if (singleChoice) {
      return labelsIndex[value as string];

      // MULTIPLE CHOICES
    } else {
      if (!value || (value && value.length === 0)) {
        return '';
      }
      return (value as string[]).map((v) => labelsIndex[v]).join(', ');
    }
  };

  const onShowDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Recompute the trigger's viewport position at open time. The cached `rect` is only refreshed
    // on mount + window resize, so on a long/scrolled page it goes stale and the menu would render
    // at the trigger's original (pre-scroll) coordinates.
    let position = { x: rect?.x, y: rect?.y + rect.height };
    if (dropdownRef.current) {
      const liveRect = dropdownRef.current.getBoundingClientRect();
      setRect({
        x: liveRect.left,
        y: liveRect.top,
        width: liveRect.width,
        height: liveRect.height
      });
      position = { x: liveRect.left, y: liveRect.top + liveRect.height };
    }
    setOptionsState({
      show: true,
      position
    });
  };

  return (
    <div ref={dropdownRef} className='w-full'>
      {displayMode === 'dropdown' && (
        <>
          <div
            className={classNames(
              setupForDark('bg-text-input-default'),
              'px-sm py-xs',
              'cursor-pointer',
              'rounded-xs',
              'flex flex-row justify-between items-center',
              'P2M',
              setupForDark('text-secondary'),
              triggerClassName
            )}
            onClick={onShowDropdown}
          >
            <div>{showSelected(inputValue)}</div>
            <ChevronDownIcon size={24} className={classNames(setupForDark('text-tertiary'))} />
          </div>
          {error && !onError && <InputErrorMessage error={error} />}
        </>
      )}
      {displayMode === 'minimalistic' && (
        <>
          <div
            className={classNames(
              setupForDark('bg-variable-dropdown'),
              setupForDark('text-black'),
              'px-xs rounded-xxs',
              'bg-opacity-15',
              'cursor-pointer',
              'flex flex-row justify-between items-center',
              'I2R',
              triggerClassName
            )}
            onClick={onShowDropdown}
          >
            <div className='max-w-[200px] truncate'>{showSelected(inputValue)}</div>
            <ChevronDownIcon size={16} />
          </div>
          {error && !onError && <InputErrorMessage error={error} />}
        </>
      )}
      {displayMode === 'task-filter' && (
        <button
          type='button'
          className={classNames(
            'flex items-center justify-center',
            'py-xs px-md',
            'h-[36px] rounded-sm',
            'cursor-pointer hover:opacity-80 transition-opacity',
            setupForDark('bg-toggle-btn-unselected text-icon-toggle-btn-unselected'),
            triggerClassName
          )}
          onClick={onShowDropdown}
        >
          <FilterIcon size={20} />
        </button>
      )}
      {displayMode === 'filter' && (
        <div
          className={classNames(
            'px-md py-xs',
            'cursor-pointer',
            'rounded-xxxl',
            'border-[1px]',
            'flex flex-row justify-between items-center',
            'P3M',
            'w-fit',
            setupForDark('text-tertiary'),
            setupForDark('border-primary')
          )}
          onClick={onShowDropdown}
        >
          <div>{showSelected(inputValue)}</div>
          <ChevronDownIcon size={16} className={classNames(setupForDark('text-tertiary'))} />
        </div>
      )}
      {displayMode === 'classical' && (
        <div
          className={classNames(
            'w-full',
            'flex flex-row justify-between items-center',
            'p-sm rounded-xs border-[1px]',
            'P2R',
            setupForDark('text-primary border-dark bg-text-input-default'),
            setupForDark(
              'disabled:text-tertiary disabled:border-text-input-disabled disabled:bg-text-input-disabled'
            ),
            className
          )}
          onClick={onShowDropdown}
        >
          <div>{showSelected(inputValue)}</div>
          <ChevronDownIcon size={16} className={classNames(setupForDark('text-tertiary'))} />
        </div>
      )}
      {isMobile && (
        <BottomSheet
          isOpen={optionsState.show}
          title={title}
          onClose={() =>
            setOptionsState({
              show: false,
              position: { x: 0, y: 0 }
            })
          }
          desktopVersion='context-menu'
          desktopMenuPosition={optionsState.position}
        >
          <div className={classNames('py-md', choicesClassName)}>
            <div className='flex flex-col justify-start gap-xxs px-md max-h-[300px] overflow-auto'>
              {choices.map((choice, i) => (
                <div
                  key={i}
                  data-testid={choice.dataTestId}
                  className={classNames(
                    'p-md',
                    'cursor-pointer',
                    'rounded-xs',
                    'flex flex-row justify-between gap-xs items-center',
                    setupForDark('hover:bg-secondary')
                  )}
                  onClick={() => onChoiceChange(choice)}
                >
                  <div className={classNames(setupForDark('text-secondary'), 'P2M')}>
                    {choice.label}
                  </div>
                  {isSelected(choice) && (
                    <TickIcon size={24} className={setupForDark('text-icon-secondary')} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </BottomSheet>
      )}
      {!isMobile && optionsState.show && (
        <ContextMenu
          onClose={() =>
            setOptionsState({
              show: false,
              position: { x: 0, y: 0 }
            })
          }
          withBackground={false}
          position={optionsState.position}
        >
          {/* Cap height + scroll so long lists (e.g. country pickers) don't overflow the viewport. */}
          <div className='max-h-[320px] overflow-auto'>
            {choices.map((choice, i) => (
              <ContextMenuItem key={i} onClick={() => onChoiceChange(choice)}>
                <div
                  data-testid={choice.dataTestId}
                  className={classNames('flex flex-row justify-between gap-md items-center')}
                >
                  <div className={classNames(setupForDark('text-secondary'), 'P2M')}>
                    {choice.label}
                  </div>
                  {isSelected(choice) && (
                    <TickIcon size={24} className={setupForDark('text-icon-secondary')} />
                  )}
                  {!isSelected(choice) && <div className={classNames('w-[24px]')} />}
                </div>
              </ContextMenuItem>
            ))}
          </div>
        </ContextMenu>
      )}
    </div>
  );
}

export default Dropdown;
